import { type UserLike, getUserAvatar, getUserDisplayName } from "@/lib/user";
import Avatar, { type AvatarProps } from "@imminently/decisively-ui/Avatar";
import AvatarGroup from "@imminently/decisively-ui/AvatarGroup";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import type * as React from "react";

export interface AssigneeGroupProps {
  users: UserLike[];
  max?: number;
  size?: AvatarProps["size"];
  /** Rendered when there are no users. Pass `null` to render nothing. */
  emptyText?: React.ReactNode;
}

/**
 * Renders an {@link AvatarGroup} from an array of `UserLike` objects, with a
 * tooltip listing all display names.
 *
 * Compatible with both `AuthUser` (snake_case) and `AssigneesData` (camelCase)
 * shapes. Does **not** depend on Formik or any form library.
 */
export function AssigneeGroup({ users, max = 3, size = "sm", emptyText = "No members assigned" }: AssigneeGroupProps) {
  if (!users || users.length === 0) {
    return emptyText === null ? null : <span className="text-muted-foreground text-sm">{emptyText}</span>;
  }

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <AvatarGroup
          max={max}
          size={size}
        >
          {users.map((user, i) => {
            const src = getUserAvatar(user);
            const display = getUserDisplayName(user);
            return (
              <Avatar
                key={(user as any).id ?? (user as any).identity_id ?? i}
                src={src}
                alt={display}
                name={display}
                size={size}
                initialCount={1}
              />
            );
          })}
        </AvatarGroup>
      </Tooltip.Trigger>
      <Tooltip.Content>
        <div className="flex flex-col gap-0.5">
          {users.map((user, i) => (
            <span key={(user as any).id ?? (user as any).identity_id ?? i}>{getUserDisplayName(user)}</span>
          ))}
        </div>
      </Tooltip.Content>
    </Tooltip>
  );
}
AssigneeGroup.displayName = "AssigneeGroup";
